import { keyframes, styled } from "goober";
import { useMemo } from "react";
import { queries } from "../../../../shared/queries";
import { useQuery } from "@rocicorp/zero/react";
import { type ColumnItem, FileBrowserColumn } from "../FileBrowserColumn";

type Props = {
  spaceId: string;
  selectedId: string | null;
  focusedColumn: boolean;
  focusedIndex: number;
  columnIndex: number;
  onSelect: (
    id: string,
    label: string,
    rowIndex: number,
    meta?: Record<string, string>
  ) => void;
  onReplace?: (
    id: string,
    label: string,
    meta?: Record<string, string>
  ) => void;
  onRegisterCount: (columnIndex: number, count: number) => void;
};

const pulseOuter = keyframes`
  0% {
    opacity: 0.8;
    transform: scale(0.5);
  }
  66% {
    opacity: 0;
    transform: scale(1);
  }
  100% {
    opacity: 0;
    transform: scale(1);
  }
`;

const LiveDot = styled("div")`
  position: relative;
  width: 16px;
  height: 16px;
  flex-shrink: 0;

  &::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 16px;
    height: 16px;
    margin-left: -8px;
    margin-top: -8px;
    border-radius: 50%;
    background: var(--green-600);
    animation: ${pulseOuter} 1.5s cubic-bezier(0, 0, 0.2, 1) infinite;
  }

  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--green-600);
  }
`;

const MetaLabel = styled("span")`
  font-size: 11px;
  color: var(--text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// A clipped "Live" reads as "L…", which says nothing, so it holds its width and
// lets the name beside it truncate instead.
const LiveLabel = styled(MetaLabel)`
  flex-shrink: 0;
`;

// Flex shrinks items in proportion to weight × width, so an outsized weight
// makes the workflow name yield before the branch name does, down to a floor of
// about nine characters. Right-aligning keeps a name shorter than that floor
// flush with the chevron instead of stranded mid-row.
const WorkflowLabel = styled(MetaLabel)`
  max-width: 120px;
  min-width: 9ch;
  flex-shrink: 20;
  text-align: right;
`;

export function BranchesColumn({
  spaceId,
  selectedId,
  focusedColumn,
  focusedIndex,
  columnIndex,
  onSelect,
  onReplace,
  onRegisterCount,
}: Props) {
  const [space] = useQuery(queries.loadSpace({ spaceId }));
  const [volumes] = useQuery(queries.spaceVolumes({ spaceId }));

  const items: ColumnItem[] = useMemo(() => {
    const withVolumes = new Set(volumes.map((v) => v.branchId));

    const workflows = space?.workflows ?? [];
    const mains: ColumnItem[] = [];
    const drafts: {
      id: string;
      name: string;
      workflowName: string;
      createdAt: number;
    }[] = [];

    for (const w of workflows) {
      for (const b of w.branches ?? []) {
        if (!withVolumes.has(b.id)) continue;
        if (b.name === "main") {
          mains.push({
            id: b.id,
            label: w.name,
            meta: {
              branchKind: "live",
              branchName: b.name,
              workflowName: w.name,
            },
            left: <LiveDot />,
            right: <LiveLabel>Live</LiveLabel>,
          });
        } else {
          drafts.push({
            id: b.id,
            name: b.name,
            workflowName: w.name,
            createdAt: b.createdAt,
          });
        }
      }
    }

    mains.sort((a, b) => a.label.localeCompare(b.label));
    drafts.sort((a, b) => b.createdAt - a.createdAt);

    return [
      ...mains,
      ...drafts.map<ColumnItem>((b) => ({
        id: b.id,
        label: b.name,
        meta: {
          branchKind: "draft",
          branchName: b.name,
          workflowName: b.workflowName,
        },
        icon: "git-branch" as const,
        right: <WorkflowLabel>{b.workflowName}</WorkflowLabel>,
      })),
    ];
  }, [space, volumes]);

  return (
    <FileBrowserColumn
      heading="Versions"
      items={items}
      selectedId={selectedId}
      focusedColumn={focusedColumn}
      focusedIndex={focusedIndex}
      columnIndex={columnIndex}
      onSelect={onSelect}
      onReplace={onReplace}
      onRegisterCount={onRegisterCount}
    />
  );
}
